export const CONTENT_RIGHTS_LIMITS = {
    marketCountry: 3,
    region: 64,
};
export const RIGHTS_OPEN_ENDED = "9999-12-31T23:59:59.999Z";
export const WHOLE_MARKET = "";
export const DEFAULT_MAX_CONCURRENT_STREAMS = 0;
function parsedTime(value) {
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value.getTime();
    }
    if (typeof value !== "string" || value.trim() === "")
        return null;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}
export function isOpenEnded(availableTo) {
    const ms = parsedTime(availableTo);
    return ms !== null && ms >= Date.parse(RIGHTS_OPEN_ENDED);
}
export function availabilityFor(input, now = new Date()) {
    const rows = input.contentRights;
    if (rows === undefined) {
        return {
            state: "unknown",
            gate: "unknown",
            availableIn: [],
            markets: [],
            nextChangeAt: null,
        };
    }
    if (rows.length === 0) {
        return {
            state: "unrestricted",
            gate: "none",
            availableIn: [],
            markets: [],
            nextChangeAt: null,
        };
    }
    const at = now.getTime();
    const boundaries = [];
    const markets = marketRows(rows, at, boundaries);
    const availableIn = [
        ...new Set(markets.filter((m) => m.state === "available").map((m) => m.marketCountry)),
    ].sort();
    const state = markets.some((m) => m.state === "available")
        ? "available"
        : markets.some((m) => m.state === "scheduled")
            ? "scheduled"
            : "expired";
    const next = boundaries.filter((ms) => ms > at).sort((a, b) => a - b)[0];
    return {
        state,
        gate: "contentRights",
        availableIn,
        markets,
        nextChangeAt: next === undefined ? null : new Date(next).toISOString(),
    };
}
function marketRows(rows, at, boundaries) {
    return rows.map((row) => {
        var _a, _b, _c, _d;
        const from = parsedTime(row.availableFrom);
        const to = parsedTime(row.availableTo);
        if (from !== null)
            boundaries.push(from);
        if (to !== null && !isOpenEnded(row.availableTo))
            boundaries.push(to + 1);
        const state = from === null || to === null
            ? "expired"
            : at < from
                ? "scheduled"
                : at > to
                    ? "expired"
                    : "available";
        return {
            marketCountry: String((_a = row.marketCountry) !== null && _a !== void 0 ? _a : ""),
            region: String((_b = row.region) !== null && _b !== void 0 ? _b : ""),
            state,
            availableFrom: String((_c = row.availableFrom) !== null && _c !== void 0 ? _c : ""),
            availableTo: String((_d = row.availableTo) !== null && _d !== void 0 ? _d : ""),
            endsAt: isOpenEnded(row.availableTo) ? null : String(row.availableTo),
        };
    });
}
const EXPIRY_WARNING_MS = 30 * 24 * 60 * 60 * 1000;
export function rightsIssues(asset, now = new Date()) {
    var _a;
    const rights = asset.contentRights;
    if (!rights || rights.length === 0)
        return [];
    const out = [];
    const rightsSaysEncrypted = rights.some((r) => r.encrypted === true);
    const mediaSaysEncrypted = ((_a = asset.medias) !== null && _a !== void 0 ? _a : []).some((media) => { var _a; return ((_a = media.mediaResources) !== null && _a !== void 0 ? _a : []).some((r) => r.isEncrypted === true); });
    if (rightsSaysEncrypted !== mediaSaysEncrypted) {
        out.push({
            code: "encrypted_flag_disagrees_with_media",
            detail: { contentRights: rightsSaysEncrypted, media: mediaSaysEncrypted },
        });
    }
    const availability = availabilityFor(asset, now);
    if (availability.state !== "available") {
        out.push({
            code: "no_market_available_now",
            detail: { state: availability.state },
        });
    }
    else {
        const at = now.getTime();
        const soonest = availability.markets
            .filter((m) => m.state === "available" && m.endsAt !== null)
            .map((m) => Date.parse(m.endsAt))
            .filter((ms) => !Number.isNaN(ms) && ms - at <= EXPIRY_WARNING_MS)
            .sort((a, b) => a - b)[0];
        if (soonest !== undefined) {
            out.push({
                code: "expires_within_30_days",
                detail: { endsAt: new Date(soonest).toISOString() },
            });
        }
    }
    return out;
}
function isoOf(ms) {
    return new Date(ms).toISOString();
}
function readDate(value, field, index, endOfWindow) {
    const raw = typeof value === "string" ? value.trim() : value;
    if (raw instanceof Date || typeof raw === "string") {
        if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            if (endOfWindow) {
                return {
                    refusal: {
                        error: "ambiguous_date",
                        field,
                        index,
                        value: raw,
                        hint: `A bare date as ${field} is 24 hours ambiguous: "${raw}" reads as the END of ${raw} to whoever signed the licence and as the START of it to a date parser. Send a timestamp - "${raw}T23:59:59.999Z" for the end of that day, or "${raw}T00:00:00.000Z" if the licence really stops as the day begins.`,
                    },
                };
            }
            return { value: Date.parse(`${raw}T00:00:00.000Z`) };
        }
        const ms = parsedTime(raw);
        if (ms !== null)
            return { value: ms };
    }
    return {
        refusal: {
            error: "invalid_date",
            field,
            index,
            value,
            hint: "An ISO 8601 timestamp, e.g. 2026-03-01T00:00:00.000Z.",
        },
    };
}
export function readContentRights(raw, now = new Date()) {
    var _a;
    if (!Array.isArray(raw)) {
        return { refusal: { error: "content_rights_must_be_array" } };
    }
    const out = [];
    const windows = new Map();
    for (const [index, entry] of raw.entries()) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
            return { refusal: { error: "content_rights_row_must_be_object", index } };
        }
        const row = entry;
        const marketCountry = typeof row.marketCountry === "string"
            ? row.marketCountry.trim().toLowerCase()
            : "";
        if (!/^[a-z]{2,3}$/.test(marketCountry)) {
            return {
                refusal: {
                    error: "invalid_market_country",
                    index,
                    value: row.marketCountry,
                    hint: 'marketCountry is VarChar(3): a lowercase 2-3 letter country code, no region suffix. It is also the ONLY thing that switches the catalogue\'s availability filter on - a window with no market is a window nothing applies. "SE-STO" cannot be stored.',
                },
            };
        }
        const region = typeof row.region === "string" ? row.region.trim() : WHOLE_MARKET;
        if (region.length > CONTENT_RIGHTS_LIMITS.region) {
            return {
                refusal: {
                    error: "field_too_long",
                    field: "region",
                    index,
                    max: CONTENT_RIGHTS_LIMITS.region,
                },
            };
        }
        let from = now.getTime();
        if (row.availableFrom !== undefined && row.availableFrom !== null) {
            const read = readDate(row.availableFrom, "availableFrom", index, false);
            if ("refusal" in read)
                return read;
            from = read.value;
        }
        let to = Date.parse(RIGHTS_OPEN_ENDED);
        if (row.availableTo !== undefined && row.availableTo !== null) {
            const read = readDate(row.availableTo, "availableTo", index, true);
            if ("refusal" in read)
                return read;
            to = read.value;
        }
        if (to <= from) {
            return {
                refusal: {
                    error: "window_ends_before_it_starts",
                    index,
                    availableFrom: isoOf(from),
                    availableTo: isoOf(to),
                    hint: "A window that closes before it opens licenses nothing, and the catalogue's filter (availableFrom <= now <= availableTo) can never match it. Omit availableTo for an open-ended licence.",
                },
            };
        }
        if (row.visibleFrom !== undefined && row.visibleFrom !== null) {
            const read = readDate(row.visibleFrom, "visibleFrom", index, false);
            if ("refusal" in read)
                return read;
            if (read.value !== from) {
                return {
                    refusal: {
                        error: "visible_from_is_inert",
                        index,
                        visibleFrom: isoOf(read.value),
                        availableFrom: isoOf(from),
                        hint: "visibleFrom has zero readers in the entire Content API - nothing shows, hides or delays anything because of it. Foyer therefore writes it equal to availableFrom and refuses a different value rather than promising a visible-then-playable lifecycle that no consumer implements. Availability is ONE window. See docs/distribution-availability.md.",
                    },
                };
            }
        }
        const key = `${marketCountry} ${region.toLowerCase()}`;
        const existing = (_a = windows.get(key)) !== null && _a !== void 0 ? _a : [];
        const clash = existing.find((w) => w.from <= to && from <= w.to);
        if (clash) {
            return {
                refusal: {
                    error: "overlapping_rights_window",
                    index,
                    marketCountry,
                    region,
                    conflictsWith: {
                        availableFrom: isoOf(clash.from),
                        availableTo: isoOf(clash.to),
                    },
                    hint: "Two windows for one market and region overlap. Upstream matches with a `some()`, so both would be in force at once and neither would be wrong - which leaves nobody able to say what this title is licensed for. Merge them, or split the market by region.",
                },
            };
        }
        existing.push({ from, to });
        windows.set(key, existing);
        const maxConcurrentStreams = row.maxConcurrentStreams === undefined || row.maxConcurrentStreams === ""
            ? DEFAULT_MAX_CONCURRENT_STREAMS
            : Number(row.maxConcurrentStreams);
        if (!Number.isFinite(maxConcurrentStreams) ||
            maxConcurrentStreams < 0 ||
            !Number.isInteger(maxConcurrentStreams)) {
            return {
                refusal: {
                    error: "invalid_max_concurrent_streams",
                    index,
                    value: row.maxConcurrentStreams,
                    hint: "A whole number of streams, or 0 for 'not limited'. Nothing upstream reads this field, so it records an intention rather than enforcing one.",
                },
            };
        }
        const built = {
            marketCountry,
            region,
            visibleFrom: isoOf(from),
            availableFrom: isoOf(from),
            availableTo: isoOf(to),
            encrypted: row.encrypted === true,
            maxConcurrentStreams,
            allowedToDownload: row.allowedToDownload === true,
        };
        const id = row.contentRightsId;
        if (typeof id === "number" && Number.isInteger(id) && id > 0) {
            out.push({ ...built, contentRightsId: id });
        }
        else {
            out.push(built);
        }
    }
    return { value: out };
}
export function rightsSnapshot(rows) {
    return (rows !== null && rows !== void 0 ? rows : [])
        .map((row) => {
        var _a, _b, _c, _d;
        return ({
            marketCountry: String((_a = row.marketCountry) !== null && _a !== void 0 ? _a : ""),
            region: String((_b = row.region) !== null && _b !== void 0 ? _b : ""),
            availableFrom: String((_c = row.availableFrom) !== null && _c !== void 0 ? _c : ""),
            availableTo: String((_d = row.availableTo) !== null && _d !== void 0 ? _d : ""),
        });
    })
        .sort((a, b) => `${a.marketCountry}${a.region}${a.availableFrom}`.localeCompare(`${b.marketCountry}${b.region}${b.availableFrom}`));
}
export function rightsChanged(before, after) {
    return (JSON.stringify(rightsSnapshot(before)) !==
        JSON.stringify(rightsSnapshot(after)));
}
export function withdrawnRights(input, now = new Date()) {
    var _a;
    const rows = (_a = input.contentRights) !== null && _a !== void 0 ? _a : [];
    const at = new Date(now.getTime() - 1).toISOString();
    const closed = rows.map((row) => {
        var _a, _b, _c, _d;
        const from = String((_a = row.availableFrom) !== null && _a !== void 0 ? _a : at);
        const start = Date.parse(from) > Date.parse(at) ? at : from;
        return {
            marketCountry: String((_b = row.marketCountry) !== null && _b !== void 0 ? _b : WHOLE_MARKET),
            region: String((_c = row.region) !== null && _c !== void 0 ? _c : WHOLE_MARKET),
            visibleFrom: start,
            availableFrom: start,
            availableTo: at,
            encrypted: Boolean(row.encrypted),
            maxConcurrentStreams: Number((_d = row.maxConcurrentStreams) !== null && _d !== void 0 ? _d : 0),
            allowedToDownload: Boolean(row.allowedToDownload),
        };
    });
    if (closed.length > 0)
        return closed;
    return [
        {
            marketCountry: WHOLE_MARKET,
            region: WHOLE_MARKET,
            visibleFrom: at,
            availableFrom: at,
            availableTo: at,
            encrypted: false,
            maxConcurrentStreams: DEFAULT_MAX_CONCURRENT_STREAMS,
            allowedToDownload: false,
        },
    ];
}
export function isWithdrawn(input, now = new Date()) {
    return availabilityFor(input, now).state === "expired";
}
